//#region Typedefs

/**
 * @typedef {Object} BaseQuantityRestrictionData
 * @property {string} partNo
 * @property {string} partType
 * @property {number} userEnteredQuantity
 * @property {number} inCartQuantity
 * @property {boolean} isFeatured
 * @property {boolean} isNewArrival
 * @property {boolean} isDigitalDownload
 * @property {JQuery<HTMLElement>} $container - The parent ".add-to-cart-wrapper" element.
 * @property {JQuery<HTMLElement>} $target - The target element from the event.
 * @property {JQuery<HTMLElement>} $inCartIcon - The icon element to display if the item is in the cart.
 * @property {JQuery<HTMLElement>} $notInCartIcon - The icon element to hide if the item is not in the cart.
 * @property {JQuery<HTMLElement>} $quantityBadgeElement - The element that shows the incart quantity of a part.
 */

/**
 * @typedef {Object} UniqueTileQuantityRestrictionData
 * @property {"tile"} containerType
 * @property {JQuery<HTMLElement>} $addToCartBtn
 * @property {JQuery<HTMLElement>} $downloadBtn
 * @property {JQuery<HTMLElement>} $inCartMessageContainer - The element which should display the cart message.
 * @property {JQuery<HTMLElement>} $restrictionMessageContainer - The element which displays the restriction message.
 * @property {JQuery<HTMLElement>} $noticeMessageContainer - The element which displays the informational notice message(s).
 * @property {JQuery<HTMLElement>} $quantityElement
 * @property {JQuery<HTMLElement>} $backOrderMessageContainer - The element which should display the backorder message.
 * @property {JQuery<HTMLElement>} $userFavoriteContainer - The element that contains the user-favorite icon.
 * @property {boolean} showAddToCartButton
 * @property {boolean} showDownloadButton
 * @property {boolean} disableAddToCartButton
 * @property {string|null} inCartMessage - If null hides the in-cart message
 * element. Will also be hidden if the restrictionMessage is not null.
 * @property {string|null} restrictionMessage
 * @property {string|null} noticeMsg
 * @property {string|null} backorderMsg
 * @property {number} maxQuantity
 * @property {boolean} isUserFavorite
 * @property {string} downloadUrl
 */

/**
 * @typedef {BaseQuantityRestrictionData & UniqueTileQuantityRestrictionData} TileRestrictionData
 */

/**
 * @typedef {Object} UniqueListQuantityRestrictionData
 * @property {"list"} containerType
 * @property {JQuery<HTMLElement>} $addToCartBtn
 * @property {JQuery<HTMLElement>} $downloadBtn
 * @property {JQuery<HTMLElement>} $inCartMessageContainer - The element which should display the cart message.
 * @property {JQuery<HTMLElement>} $quantityElement
 * @property {JQuery<HTMLElement>} $noticeMessageContainer
 * @property {JQuery<HTMLElement>} $backOrderMessageContainer - The element which should display the backorder message.
 * @property {JQuery<HTMLElement>} $userFavoriteContainer - The element that contains the user-favorite icon.
 * @property {boolean} showAddToCartButton
 * @property {boolean} showDownloadButton
 * @property {boolean} showQuantityInput
 * @property {boolean} disableAddToCartButton
 * @property {string|null} inCartMessage - If null hides the in-cart message
 * element. Will also be hidden if the restrictionMessage is not null.
 * @property {string|null} restrictionMessage
 * @property {string} [noticeMsg]
 * @property {string} [backorderMsg]
 * @property {boolean} isUserFavorite
 * @property {string} downloadUrl
 */

/**
 * @typedef {BaseQuantityRestrictionData & UniqueListQuantityRestrictionData} ListRestrictionData
 */

/**
 * @typedef {Object} UniqueGlobalSearchQuantityRestrictionData
 * @property {"global"} containerType
 * @property {JQuery<HTMLElement>} $addToCartBtn
 * @property {JQuery<HTMLElement>} $downloadBtn
 * @property {JQuery<HTMLElement>} $inCartMessageContainer - The element which should display the cart message.
 * @property {JQuery<HTMLElement>} $restrictionMessageContainer - The element which displays the restriction message.
 * @property {JQuery<HTMLElement>} $noticeMessageContainer - The element which displays the informational notice message(s).
 * @property {JQuery<HTMLElement>} $quantityElement
 * @property {JQuery<HTMLElement>} $backOrderMessageContainer - The element which should display the backorder message.
 * @property {JQuery<HTMLElement>} $userFavoriteContainer - The element that contains the user-favorite icon.
 * @property {boolean} disableAddToCartButton
 * @property {boolean} showAddToCartButton
 * @property {boolean} showDownloadButton
 * @property {string|null} inCartMessage If null hides the in-cart message
 * element. Will also be visible even if the restrictionMessage is not
 * null.
 * @property {string|null} restrictionMessage
 * @property {string|null} noticeMsg
 * @property {string|null} backorderMsg
 * @property {number} maxQuantity
 * @property {boolean} isUserFavorite
 * @property {string} downloadUrl
 */

/**
 * @typedef {BaseQuantityRestrictionData & UniqueGlobalSearchQuantityRestrictionData} GlobalSearchRestrictionData
 */

/**
 * @typedef {Object} UniqueProductHeroQuantityRestrictionData
 * @property {"product-hero"} containerType
 * @property {JQuery<HTMLElement>} $addToCartBtn
 * @property {JQuery<HTMLElement>} $downloadBtn
 * @property {JQuery<HTMLElement>} $inCartMessageContainer - The element which should display the cart message.
 * @property {JQuery<HTMLElement>} $restrictionMessageContainer - The element which displays the restriction message.
 * @property {JQuery<HTMLElement>} $noticeMessageContainer - The element which displays the informational notice message(s).
 * @property {JQuery<HTMLElement>} $quantityElement
 * @property {JQuery<HTMLElement>} $backOrderMessageContainer - The element which should display the backorder message.
 * @property {JQuery<HTMLElement>} $userFavoriteContainer - The element that contains the user-favorite icon.
 * @property {boolean} disableAddToCartButton
 * @property {boolean} showAddToCartButton
 * @property {boolean} showDownloadButton
 * @property {string|null} inCartMessage If null hides the in-cart message
 * element. Will also be visible even if the restrictionMessage is not
 * null.
 * @property {string|null} restrictionMessage
 * @property {string|null} noticeMsg
 * @property {string|null} backorderMsg
 * @property {number} maxQuantity
 * @property {boolean} exceedsMaxQuantity
 * @property {boolean} isUserFavorite
 * @property {string} downloadUrl
 */

/**
 * @typedef {BaseQuantityRestrictionData & UniqueProductHeroQuantityRestrictionData} ProductHeroRestrictionData
 */

/**
 * @typedef {Object} UniqueLineItemQuantityRestrictionData
 * @property {"lineitem"} containerType
 * @property {JQuery<HTMLElement>} $restrictionMessage The element which displays the restriction message.
 * @property {JQuery<HTMLElement>} $restrictionAlert The alert element which contains the $restrictionMessage
 * @property {JQuery<HTMLElement>} $quantityElement
 * @property {JQuery<HTMLElement>} $noticeMessageContainer
 * @property {boolean} preventsCheckout
 * @property {string|null} restrictionMessage
 * @property {string|null} noticeMsg
 * @property {number} maxQuantity
 * @property {boolean} exceedsMaxQuantity
 */

/**
 * @typedef {BaseQuantityRestrictionData & UniqueLineItemQuantityRestrictionData} LineItemRestrictionData
 */

/**
 * @typedef {TileRestrictionData|ListRestrictionData|GlobalSearchRestrictionData|ProductHeroRestrictionData|LineItemRestrictionData} RestrictionData
 */

/**
 * @callback DebounceV2Callback
 * @param {AbortSignal} abortSignal
 * @param {...object} args
 * @returns {any}
 */

/**
 * @callback DebounceV2Response
 * @param {...object} args
 * @returns {any}
 */

//#endregion

var site = site || {};
site.Utilities = (function () {
    //#region Constants

    const RESTRICTION_TYPE_MAX_QUANTITY = "MAX_QUANTITY";
    const RESTRICTION_TYPE_BACKORDER = "BACKORDER";
    const RESTRICTION_TYPE_NOT_AVAILABLE = "NOT_AVAILABLE";
    const RESTRICTION_TYPE_HCP_EXCLUDED = "HCP_EXCLUDED";
    const RESTRICTION_TYPE_STATE = "STATE";
    const RESTRICTION_TYPE_COUNTRY = "COUNTRY";
    const RESTRICTION_TYPE_NOTICE = "NOTICE";

    //#endregion

    //#region Functions

    /**
     * Wraps a function and returns a new one with the same signature but
     * returns void. The new function will delay the execution of the original
     * function determined by the wait parameter.
     * @param {Function} fn The function to execute after the wait period.
     * @param {number} wait The time to wait before executing the function in MS.
     * @param {Function} [ondebounceFn] Optional callback which is executed if
     * the function was invoked before the wait period expired. Useful if
     * cleaning up any external resources.
     * @returns {Function}
     */
    var debounce = function (fn, wait, ondebounceFn) {
        if (fn === null || fn === undefined) {
            throw new Error("The argument \"fn\" cannot be null nor undefined.");
        } else if (wait === null || wait === undefined || wait < 1) {
            throw new Error("The argument \"wait\" cannot be null nor undefined, nor less than one.");
        }

        let timeout = null;
        let ctxArgs = null;

        function _debounce() {
            if (timeout) {
                clearTimeout(timeout);
            }

            timeout = setTimeout(onTimeout, wait);

            if (ondebounceFn) {
                if (Array.isArray(ctxArgs)) {
                    ondebounceFn(...ctxArgs);
                } else {
                    ondebounceFn(ctxArgs);
                }
            }
        }

        function onTimeout() {
            timeout = null;

            if (Array.isArray(ctxArgs)) {
                fn(...ctxArgs);
            } else {
                fn(ctxArgs);
            }
        }

        return function () {
            // Always store the latest arguments.
            const newArgs = [...arguments];

            // Either queue up the function or debounce it.
            if (timeout) {
                _debounce(newArgs);
            } else {
                timeout = setTimeout(onTimeout, wait)
            }

            // Override the previous stored arguments with the new arguments.
            ctxArgs = newArgs;
        }
    }

    /**
     * Similar to debounce except passes in a an AbortSignal as the first
     * argument to the wrapped fn.
     * @param {DebounceV2Callback} fn 
     * @param {number} wait 
     * @returns {DebounceV2Response}
     */
    var debounceV2 = function (fn, wait) {
        let timeout = null;
        let ctxArgs = null;
        let abortController = new AbortController();

        function _debounce() {
            if (timeout) {
                abortController.abort();
                abortController = new AbortController();
                clearTimeout(timeout);
            }

            timeout = setTimeout(onTimeout, wait);
        }

        function onTimeout() {
            timeout = null;

            if (Array.isArray(ctxArgs)) {
                fn(abortController.signal, ...ctxArgs);
            } else {
                fn(abortController.signal, ctxArgs);
            }

            abortController = new AbortController();
        }

        return function () {
            // Always store the latest arguments.
            const newArgs = [...arguments];

            // Either queue up the function or debounce it.
            if (timeout) {
                _debounce(newArgs);
            } else {
                timeout = setTimeout(onTimeout, wait)
            }

            // Override the previous stored arguments with the new arguments.
            ctxArgs = newArgs;
        }
    }

    var backgroundImageElement = function () {
        $('.js-bg-image').css('background-image', function () {
            var bgUrlEncode = encodeURI($(this).data("bg-img"));
            var bg = 'url("' + bgUrlEncode + '")';
            return bg;
        });
    }

    var partImageToggle = function () {
        $('.js-part-image').attr('src', $('.js-part:checked').data('img'));

        $('.js-part').on('change', function () {
            if (this.checked) {
                $('.js-part-image').attr('src', $(this).data('img'));
            }
        })
    }
    function getUrlParams() {
        var params = window.location.search.split('?')[1];
        if (params) {
            var obj = params.split("&").reduce(function (prev, curr, i, arr) {
                var p = curr.split("=");
                prev[decodeURIComponent(p[0])] = decodeURIComponent(p[1]);
                return prev;
            }, {});
            return obj;
        }
        return {};
    };

    function toggleSortByAndViewBtns(state) {
        var activeView = 'list';
        var params = getUrlParams();

        if (typeof params != 'undefined') {
            if (typeof params['searchViewType'] != 'undefined') {
                activeView = params['searchViewType'].toLocaleLowerCase();
            }
        }
        if (state == 'enable') {
            $('.dropbtn-container').removeClass('disabled-link');

            if (activeView == 'list') {
                $('#list-view-button').removeClass('disabled-link disabledBtn btn-invert').addClass('btn-primary');
                $('#tile-view-button, #global-list-view-button').removeClass('disabled-link disabledBtn btn-primary').addClass('btn-invert');
            }
            if (activeView == 'global') {
                $('#global-list-view-button').removeClass('disabled-link disabledBtn btn-invert').addClass('btn-primary');
                $('#tile-view-button, #list-view-button').removeClass('disabled-link disabledBtn btn-primary').addClass('btn-invert');
            }
            if (activeView == 'tile') {
                $('#tile-view-button').removeClass('disabled-link disabledBtn btn-invert').addClass('btn-primary');
                $('#list-view-button, #global-list-view-button').removeClass('disabled-link disabledBtn btn-primary').addClass('btn-invert');
            }
        }
        else if (state == 'disable') {
            $('.dropbtn-container').addClass('disabled-link');
            $('#list-view-button, #tile-view-button, #global-list-view-button').addClass('disabled-link disabledBtn').removeClass('btn-invert btn-primary active-view');
        }
    };


    function getUrlPathSegments() {
        return window.location.pathname.split("/");
    }

    function setProductLinks() {
        $('a[data-product-slug]').each(function () {
            $(this).attr('href', '/products/' + $(this).data('product-slug').toLowerCase());
        })
    }
    function stopVideoOnModalClose() {
        $(".js-close").click(function () {
            var thisModalVideo = $(this).next('.notch-md').find('iframe');
            var videoSrc = thisModalVideo.attr('src');
            //sets src to nothing, stops the video
            thisModalVideo.attr("src", "");
            thisModalVideo.attr("src", videoSrc);
        });
    }

    var bindNumericConstraints = function () {
        $(".numeric-input").on("keypress", function (e) {
            if (isNaN(e.key)) e.preventDefault();
        });
    }

    var onCheckout = function () {
        if ($('.checkout').is(':visible')) {
            return true;
        }
        else {
            return false;
        }
    }

    var itemsInYourCartModal = new bootstrap.Modal(document.getElementById("itemsInYourCartModal"), { backdrop: 'static' });

    var backToClassicPrep = function () {
        $("#backToClassicSiteBannerLink").click(function () {
            // If there are items in your cart, show the modal, otherwise just forward on.
            var hasCartItems = false;
            let name = "CartCount=";
            let cookies = document.cookie.split(';');

            for (var i = 0; i < cookies.length; i++) {
                var c = cookies[i];

                while (c.charAt(0) == ' ') {
                    c = c.substring(1, c.length);
                }

                if (c.indexOf(name) == 0) {
                    //cookie exists, check for 0 == have items in cart
                    if (c.substring(10, 11) != 0) {
                        hasCartItems = true;
                    }
                }
            }

            if (hasCartItems) {
                itemsInYourCartModal.show();
            }
            else {
                window.location = '/Home/BackToClassic';
            }
        });

        $("#returnToOriginalSiteAnyway").click(function () {
            window.location = '/Home/BackToClassicPartList';
        });

        $(".cancelItemsInYourCartModal").click(function () {
            $("#itemsInYourCartModal").hide();
        });
    }

    var displayToast = function (title, body) {
        $('#liveToast').find('.toast-title')[0].innerText = '';
        $('#liveToast').find('.toast-title')[0].innerText = title;
        $('#liveToast').find('.toast-body')[0].innerText = '';
        $('#liveToast').find('.toast-body')[0].innerText = body;
        var toastLiveExample = document.getElementById('liveToast');
        var toast = new bootstrap.Toast(toastLiveExample);
        toast.show();
    }

    var stringToBool = function (stringBool) {
        stringBool = stringBool ?? false;
        return stringBool.toString().toLowerCase() == "true";
    }

    /**
     * Clones the template content node and replaces all {{key}} strings with
     * the template data (if provided).
     * @param {HTMLTemplateElement} templateElement 
     * @param {object} [templateData] 
     * @param {boolean} [logMissingTemplateVariables]
     * @returns {HTMLElement}
     */
    var populateTemplate = function (templateElement, templateData, logMissingTemplateVariables) {
        /** @type {DocumentFragment} */
        const newDocumentFragment = templateElement.content.cloneNode(true);

        if (!templateData) {
            return newDocumentFragment.firstElementChild;
        }

        if (templateData) {
            let html = newDocumentFragment.firstElementChild.outerHTML;
            const expectedKeys = html.match(/\{\{(\w+)\}\}/g);
            const distinctExpectedKeys = [];

            for (let key of expectedKeys) {
                if (distinctExpectedKeys.includes(key)) {
                    continue;
                }

                distinctExpectedKeys.push(key);
            }

            for (let key of distinctExpectedKeys) {
                const cleanedKey = key.substring(2, key.length - 2);

                if (cleanedKey in templateData) {
                    html = html.replace(key, templateData[cleanedKey]?.toString() ?? "");
                } else if (logMissingTemplateVariables) {
                    console.warn("Template property not set in templateData: " + cleanedKey);
                }
            }

            const tempDiv = document.createElement("div");
            tempDiv.appendChild(newDocumentFragment.firstElementChild);
            tempDiv.firstElementChild.outerHTML = html;
            const result = tempDiv.firstElementChild;
            tempDiv.firstElementChild.remove();
            return result;
        }
    }

    /**
     * Reads all attributes that start with the attributePrefix on the element
     * and parent elements and adds them to the result object. If the result
     * object already has a key for that property then the value is converted
     * into an array and the new value appended to it.
     * @param {HTMLElement} element The HTMLElement to read the attributes of.
     * @param {string} attributePrefix Filters out all attributes that do not
     * start with this prefix.
     * @param {Object} result The object to store the attribute key/value pairs
     * in.
     * @param {Function<string, string>} [nameTransformer] Optional function
     * which transforms the attribute names. Usually used for transforming them
     * to camel/pascal case schema.
     * @returns {void}
     */
    function readElementAttributes(
        element,
        attributePrefix,
        result,
        nameTransformer
    ) {
        if (element === null || element === undefined) {
            throw new Error('The argument "element" cannot be null nor undefined.');
        } else if (attributePrefix === null || attributePrefix === undefined || attributePrefix === '') {
            throw new Error('The argument "attributePrefix" cannot be null, undefined, nor empty.');
        } else if (result === null || result === undefined) {
            throw new Error('The argument "result" cannot be null nor undefind.');
        }

        for (let attr of element.attributes) {
            if (!attr.name.startsWith(attributePrefix)) {
                continue;
            }

            let keyName = attr.name.substring(attributePrefix.length);

            if (nameTransformer) {
                keyName = nameTransformer(keyName);
            }

            // This is to decode html entities and any html encoded bits. The
            // decodeURI and decodeURIComponent functions only decode html
            // encoded strings, not entites.
            const value = $("<textarea />").html(attr.value).text();

            // Don't let parent attributes override the child attributes.
            // Instead convert it to an array and append the parent value to
            // the end of the array.
            if (keyName in result) {
                if (!Array.isArray(result[keyName])) {
                    result[keyName] = [result[keyName]];
                }

                result[keyName].push(value);
            } else {
                result[keyName] = value;
            }
        }
    }

    /**
     * Converts a string to lower snake case.
     * @param {string} str 
     * @returns {string}
     */
    function lowerSnakeCaseNameTransformer(str) {
        let convertedStr = str.replace(/[A-Z]/g, (substr) => {
            return "_" + substr.toLocaleLowerCase();
        }).replace(/[^a-z_]/g, '_').replace(/_{2,}/g, '_');

        if (convertedStr.startsWith('_')) {
            convertedStr = convertedStr.substring(1);
        }

        if (convertedStr.endsWith('_')) {
            convertedStr = convertedStr.substring(0, convertedStr.length - 1);
        }

        return convertedStr;
    }

    /**
     * Retrieves the google analytics data for an element from the data
     * attributes on the element or parent elements. Returns null if no google
     * analytics data available.
     * @param {JQuery<HTMLElement>} $element The element to try and retrieve
     * the item info for.
     * @returns {Object|null}
     */
    var getGoogleAnalyticsData = function ($element) {
        if ($element === null || $element === undefined) {
            throw new Error('The argument "$element" cannot be null nor undefind.');
        }

        const attributePrefix = 'data-ga-';
        const result = {};

        if ($element.length > 0) {
            readElementAttributes($element[0], attributePrefix, result, lowerSnakeCaseNameTransformer);
        }

        $element.parents().each(function () {
            readElementAttributes(this, attributePrefix, result, lowerSnakeCaseNameTransformer);
        });

        return result;
    }

    /**
     * Checks the restrictions on the trigger and determines returns the
     * expected html updates required.
     * @param {JQuery<HTMLElement>|HTMLElement} trigger The element triggering
     * the restriction check.
     * @param {string} selected The quantity of the part to add to the cart.
     * @returns {RestrictionData}
     */
    var determineAddToCartElementsRestrictions = function (trigger, selected) {
        const $target = $(trigger);
        const $container = $target.closest("[data-add-to-cart-container-type]");

        /** @type {"tile"|"list"|"global"|"product-hero"|"lineitem"} */
        const containerType = $container.data("add-to-cart-container-type");
        let userEnteredQuantity = parseInt(selected);

        if (Number.isNaN(userEnteredQuantity)) {
            userEnteredQuantity = 0;
        }

        /** @type {RestrictionData} */
        let result;

        if (containerType === "tile") {
            result = determineTileAddToCartRestrictions($target, userEnteredQuantity, $container);
        } else if (containerType === "list") {
            result = determineListAddToCartRestrictions($target, userEnteredQuantity, $container);
        } else if (containerType === "global") {
            result = determineGlobalSearchAddToCartRestrictions($target, userEnteredQuantity, $container);
        } else if (containerType === "product-hero") {
            result = determineProductHeroAddToCartRestrictions($target, userEnteredQuantity, $container);
        } else if (containerType === "lineitem") {
            result = determineLineItemSearchAddToCartRestrictions($target, userEnteredQuantity, $container);
        } else {
            throw new Error("Unsupported container type: " + (containerType ?? "null"));
        }

        return result;
    }

    /**
     * Gets the expected actions for the tile add-to-cart container.
     * @param {JQuery<HTMLElement>} $target The element triggering
     * the restriction check.
     * @param {number} userEnteredQuantity The quantity of the part to add to
     * the cart.
     * @param {JQuery<HTMLElement>} $container
     * @returns {TileRestrictionData}
     */
    function determineTileAddToCartRestrictions($target, userEnteredQuantity, $container) {
        const $inCartMessageContainer = $container.find(".js-cart-message");
        const $quantityElement = $container.find(".quantity-select");
        const $restrictionMessageContainer = $container.find(".restriction-message-container");
        const $partDataElement = $container.find(".parts-dropdown :selected");
        const $notInCartIcon = $container.find(".not-in-cart-icon");
        const $inCartIcon = $container.find(".in-cart-icon");
        const $quantityBadgeElement = $container.find(".in-cart-count-badge");
        const $noticeMessageContainer = $container.find(".notice-message");
        const $backOrderMessageContainer = $container.find(".backorder-message");
        const $userFavoriteContainer = $container.find(".js-favorite");
        const $downloadBtn = $container.find(".js-download");

        // Error if the $partDataElement was unable to be located.
        if ($partDataElement.length === 0) {
            throw new Error("Failed to lookup the part data element.");
        }

        let {
            partMaxquantity: maxQuantityStr,
            addBtn: addToCartButtonId,
            incart: inCartStr,
            userFavorite: isUserFavoriteStr,
            part: partNo,
            partType: partType,
            downloadUrl: downloadUrl,
            isFeatured: isFeaturedStr,
            isNewArrival: isNewArrivalStr,
            isDigitalDownload: isDigitalDownloadStr,
            ...remainingPartDataProps
        } = $partDataElement[0].dataset;

        let inCart = parseInt(inCartStr);
        const isUserFavorite = isUserFavoriteStr === "true";
        const isNewArrival = isNewArrivalStr === "true";
        const isFeatured = isFeaturedStr === "true";
        const isDigitalDownload = isDigitalDownloadStr === "true";
        let maxQuantity = 2000;

        if (Number.isNaN(inCart)) {
            inCart = 0;
        }

        if (typeof(maxQuantityStr) === "string") {
            maxQuantity = parseInt(maxQuantityStr);
        } else if (typeof(maxQuantityStr) === "number") {
            maxQuantity = maxQuantityStr;
        }

        const restrictionMessages = getRestrictionMessagesFromPartData(remainingPartDataProps);
        const totalQty = userEnteredQuantity + inCart;
        const exceedsMaxQuantity = totalQty > maxQuantity;
        let disableAddToCartButton = false;

        /** @type {string|null} */
        let inCartMessage = null;

        /** @type {string|null} */
        let restrictionMessage = null;

        /** @type {string|null} */
        let noticeMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_NOTICE)
            ?.message;

        let backorderMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_BACKORDER)
            ?.message;
        // Check the max quantity restriction first.
        if (exceedsMaxQuantity) {
            const maxQuantityRestriction = restrictionMessages.find(
                x => x.restrictionType === RESTRICTION_TYPE_MAX_QUANTITY
            );

            restrictionMessage = maxQuantityRestriction?.message;
            disableAddToCartButton = true;
        } else {
            // See if there are other blocking restrictions.
            const blockingRestrictions = restrictionMessages.filter(x => x.blocksOrdering);

            if (blockingRestrictions.length > 0) {
                restrictionMessage = blockingRestrictions[0].message;
                disableAddToCartButton = true;
            }
        }

        // Hide the add-to-cart button if non-orderable.
        let showAddToCartButton = !(isDigitalDownload && partType === "NON");
        let showDownloadButton = downloadUrl !== null && downloadUrl !== "";

        // If the in-cart quantity is at the max, do not enable the add-to-cart
        // button.
        if (inCart === maxQuantity) {
            disableAddToCartButton = true;
        }

        /** @type {TileRestrictionData} */
        const result = {
            containerType: "tile",
            partNo,
            partType,
            userEnteredQuantity,
            inCartQuantity: inCart,
            $container,
            $target,
            $addToCartBtn: $("#" + addToCartButtonId),
            $downloadBtn,
            $inCartMessageContainer,
            $quantityElement,
            $restrictionMessageContainer,
            $notInCartIcon,
            $inCartIcon,
            $quantityBadgeElement,
            $noticeMessageContainer,
            $backOrderMessageContainer,
            $userFavoriteContainer,
            showAddToCartButton,
            showDownloadButton,
            disableAddToCartButton,
            restrictionMessage,
            inCartMessage,
            noticeMsg,
            backorderMsg,
            maxQuantity,
            isUserFavorite,
            isNewArrival,
            isFeatured,
            isDigitalDownload,
            downloadUrl,
        };

        return result;
    }

    /**
     * Gets the expected actions for the list add-to-cart container.
     * @param {JQuery<HTMLElement>} $target The element triggering
     * the restriction check.
     * @param {number} userEnteredQuantity The quantity of the part to add to
     * the cart.
     * @param {JQuery<HTMLElement>} $container
     * @returns {ListRestrictionData}
     */
    function determineListAddToCartRestrictions($target, userEnteredQuantity, $container) {
        const $inCartMessageContainer = $container.find(".js-cart-message");
        const $quantityElement = $container.find(".quantity-select");
        const $partDataElement = $container.find(".item-part-number-data");
        const $notInCartIcon = $container.find(".not-in-cart-icon");
        const $inCartIcon = $container.find(".in-cart-icon");
        const $quantityBadgeElement = $container.find(".in-cart-count-badge");
        const $noticeMessageContainer = $container.find(".notice-message");
        const $backOrderMessageContainer = $container.find(".backorder-message");
        const $userFavoriteContainer = $container.find(".js-favorite");
        const $downloadBtn = $container.find(".js-download");

        // Error if the $partDataElement was unable to be located.
        if ($partDataElement.length === 0) {
            throw new Error("Failed to lookup the part data element.");
        }

        let {
            partMaxquantity: maxQuantityStr,
            addBtn: addToCartButtonId,
            incart: inCartStr,
            userFavorite: isUserFavoriteStr,
            isNewArrival: isNewArrivalStr,
            isFeatured: isFeaturedStr,
            isDigitalDownload: isDigitalDownloadStr,
            part: partNo,
            partType: partType,
            downloadUrl: downloadUrl,
            ...remainingPartDataProps
        } = $partDataElement[0].dataset;

        let inCart = parseInt(inCartStr);
        const isUserFavorite = isUserFavoriteStr === "true";
        const isNewArrival = isNewArrivalStr === "true";
        const isFeatured = isFeaturedStr === "true";
        const isDigitalDownload = isDigitalDownloadStr === "true";
        let maxQuantity = 2000;

        if (Number.isNaN(inCart)) {
            inCart = 0;
        }

        if (typeof(maxQuantityStr) === "string") {
            maxQuantity = parseInt(maxQuantityStr);
        } else if (typeof(maxQuantityStr) === "number") {
            maxQuantity = maxQuantityStr;
        }

        const restrictionMessages = getRestrictionMessagesFromPartData(remainingPartDataProps);
        const totalQty = userEnteredQuantity + inCart;
        const exceedsMaxQuantity = totalQty > maxQuantity;
        let disableAddToCartButton = false;
        let showAddToCartButton = true;
        let showQuantityInput = true;
        let showDownloadButton = downloadUrl !== null && downloadUrl !== "";

        /** @type {string|null} */
        let inCartMessage = null;

        /** @type {string|null} */
        let restrictionMessage = null;

        /** @type {string|null} */
        let noticeMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_NOTICE)
            ?.message;

        let backorderMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_BACKORDER)
            ?.message;

        const blockingRestrictions = restrictionMessages.filter(
            x => x.restrictionType !== RESTRICTION_TYPE_MAX_QUANTITY
                && x.blocksOrdering
        );

        // Check the max quantity restriction first.
        if (exceedsMaxQuantity) {
            const maxQuantityRestriction = restrictionMessages.find(
                x => x.restrictionType === RESTRICTION_TYPE_MAX_QUANTITY
            );

            restrictionMessage = maxQuantityRestriction?.message;
            disableAddToCartButton = true;
            showAddToCartButton = true;
        } else {
            // See if there are other blocking restrictions besides the
            // max-quantity.
            if (blockingRestrictions.length > 0) {
                restrictionMessage = blockingRestrictions[0].message;
                disableAddToCartButton = true;
                showAddToCartButton = true;
            }
        }

        // Need to check if the part is a non-orderable part. If so need to
        // hide the add-to-cart button.
        if (isDigitalDownload && partType === "NON") {
            showAddToCartButton = false;
        }

        // If the in-cart quantity is at the max, do not enable the add-to-cart
        // button.
        if (inCart === maxQuantity) {
            disableAddToCartButton = true;
        }

        /** @type {ListRestrictionData} */
        const result = {
            containerType: "list",
            partNo,
            partType,
            userEnteredQuantity,
            inCartQuantity: inCart,
            $container,
            $target,
            $addToCartBtn: $("#" + addToCartButtonId),
            $downloadBtn,
            $noticeMessageContainer,
            $backOrderMessageContainer,
            $inCartMessageContainer,
            $quantityElement,
            $notInCartIcon,
            $inCartIcon,
            $quantityBadgeElement,
            $userFavoriteContainer,
            disableAddToCartButton,
            showAddToCartButton,
            showDownloadButton,
            restrictionMessage,
            noticeMsg,
            backorderMsg,
            inCartMessage,
            isUserFavorite,
            showQuantityInput,
            isFeatured,
            isNewArrival,
            isDigitalDownload,
            downloadUrl,
        };

        return result;
    }

    /**
     * Gets the expected actions for the global-search add-to-cart container.
     * @param {JQuery<HTMLElement>} $target The element triggering
     * the restriction check.
     * @param {number} userEnteredQuantity The quantity of the part to add to
     * the cart.
     * @param {JQuery<HTMLElement>} $container
     * @returns {GlobalSearchRestrictionData}
     */
    function determineGlobalSearchAddToCartRestrictions($target, userEnteredQuantity, $container) {
        const $inCartMessageContainer = $container.find(".js-cart-message");
        const $quantityElement = $container.find(".quantity-select");
        const $restrictionMessageContainer = $container.find(".restriction-message-container");
        const $partDataElement = $container.find(".parts-dropdown :selected");
        const $notInCartIcon = $container.find(".not-in-cart-icon");
        const $inCartIcon = $container.find(".in-cart-icon");
        const $quantityBadgeElement = $container.find(".in-cart-count-badge");
        const $noticeMessageContainer = $container.find(".notice-message");
        const $backOrderMessageContainer = $container.find(".backorder-message");
        const $userFavoriteContainer = $container.find(".js-favorite");
        const $downloadBtn = $container.find(".js-download");

        // Error if the $partDataElement was unable to be located.
        if ($partDataElement.length === 0) {
            throw new Error("Failed to lookup the part data element.");
        }

        let {
            partMaxquantity: maxQuantityStr,
            addBtn: addToCartButtonId,
            incart: inCartStr,
            userFavorite: isUserFavoriteStr,
            part: partNo,
            partType: partType,
            downloadUrl: downloadUrl,
            isNewArrival: isNewArrivalStr,
            isFeatured: isFeaturedStr,
            isDigitalDownload: isDigitalDownloadStr,
            ...remainingPartDataProps
        } = $partDataElement[0].dataset;

        let inCart = parseInt(inCartStr);
        const isUserFavorite = isUserFavoriteStr === "true";
        const isNewArrival = isNewArrivalStr === "true";
        const isFeatured = isFeaturedStr === "true";
        const isDigitalDownload = isDigitalDownloadStr === "true";
        let maxQuantity = 2000;

        if (Number.isNaN(inCart)) {
            inCart = 0;
        }

        if (typeof(maxQuantityStr) === "string") {
            maxQuantity = parseInt(maxQuantityStr);
        } else if (typeof(maxQuantityStr) === "number") {
            maxQuantity = maxQuantityStr;
        }

        const restrictionMessages = getRestrictionMessagesFromPartData(remainingPartDataProps);

        const totalQty = userEnteredQuantity + inCart;
        const exceedsMaxQuantity = totalQty > maxQuantity;
        let disableAddToCartButton = false;

        /** @type {string|null} */
        let inCartMessage = null;

        /** @type {string|null} */
        let restrictionMessage = null;

        /** @type {string|null} */
        let noticeMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_NOTICE)
            ?.message;

        let backorderMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_BACKORDER)
            ?.message;
        // Check the max quantity restriction first.
        if (exceedsMaxQuantity) {
            const maxQuantityRestriction = restrictionMessages.find(
                x => x.restrictionType === RESTRICTION_TYPE_MAX_QUANTITY
            );

            restrictionMessage = maxQuantityRestriction?.message;
            disableAddToCartButton = true;
        } else {
            // See if there are other blocking restrictions.
            const blockingRestrictions = restrictionMessages.filter(x => x.blocksOrdering);

            if (blockingRestrictions.length > 0) {
                restrictionMessage = blockingRestrictions[0].message;
                disableAddToCartButton = true;
            }
        }

        // If the in-cart quantity is at the max, do not enable the add-to-cart
        // button.
        if (inCart === maxQuantity) {
            disableAddToCartButton = true;
        }

        let showAddToCartButton = !(isDigitalDownload && partType === "NON");
        let showDownloadButton = downloadUrl !== null && downloadUrl !== "";

        /** @type {GlobalSearchRestrictionData} */
        const result = {
            containerType: "global",
            partNo,
            partType,
            userEnteredQuantity,
            inCartQuantity: inCart,
            $container,
            $target,
            $addToCartBtn: $("#" + addToCartButtonId),
            $downloadBtn,
            $inCartMessageContainer,
            $quantityElement,
            $restrictionMessageContainer,
            $notInCartIcon,
            $inCartIcon,
            $quantityBadgeElement,
            $noticeMessageContainer,
            $backOrderMessageContainer,
            $userFavoriteContainer,
            showAddToCartButton,
            showDownloadButton,
            disableAddToCartButton,
            restrictionMessage,
            inCartMessage,
            noticeMsg,
            backorderMsg,
            maxQuantity,
            isUserFavorite,
            isNewArrival,
            isFeatured,
            isDigitalDownload,
            downloadUrl,
        };

        return result;
    }

    /**
     * Gets the expected actions for the product-hero add-to-cart container.
     * @param {JQuery<HTMLElement>} $target The element triggering
     * the restriction check.
     * @param {number} userEnteredQuantity The quantity of the part to add to
     * the cart.
     * @param {JQuery<HTMLElement>} $container
     * @returns {ProductHeroRestrictionData}
     */
    function determineProductHeroAddToCartRestrictions($target, userEnteredQuantity, $container) {
        const $inCartMessageContainer = $container.find(".js-cart-message");
        const $quantityElement = $container.find(".quantity-select");
        const $restrictionMessageContainer = $container.find(".restriction-message-container");
        const $partDataElement = $container.find(".parts-dropdown :selected");
        const $notInCartIcon = $container.find(".not-in-cart-icon");
        const $inCartIcon = $container.find(".in-cart-icon");
        const $quantityBadgeElement = $container.find(".in-cart-count-badge");
        const $noticeMessageContainer = $container.find(".notice-message");
        const $backOrderMessageContainer = $container.find(".backorder-message");
        const $userFavoriteContainer = $container.parent().find(".js-favorite");
        const $downloadBtn = $container.find(".js-download");

        // Error if the $partDataElement was unable to be located.
        if ($partDataElement.length === 0) {
            throw new Error("Failed to lookup the part data element.");
        }

        let {
            partMaxquantity: maxQuantityStr,
            addBtn: addToCartButtonId,
            incart: inCartStr,
            userFavorite: isUserFavoriteStr,
            part: partNo,
            partType: partType,
            downloadUrl: downloadUrl,
            isNewArrival: isNewArrivalStr,
            isFeatured: isFeaturedStr,
            isDigitalDownload: isDigitalDownloadStr,
            ...remainingPartDataProps
        } = $partDataElement[0].dataset;

        let inCart = parseInt(inCartStr);
        const isUserFavorite = isUserFavoriteStr === "true";
        const isNewArrival = isNewArrivalStr === "true";
        const isFeatured = isFeaturedStr === "true";
        const isDigitalDownload = isDigitalDownloadStr === "true";
        let maxQuantity = 2000;

        if (Number.isNaN(inCart)) {
            inCart = 0;
        }

        if (typeof(maxQuantityStr) === "string") {
            maxQuantity = parseInt(maxQuantityStr);
        } else if (typeof(maxQuantityStr) === "number") {
            maxQuantity = maxQuantityStr;
        }

        const restrictionMessages = getRestrictionMessagesFromPartData(remainingPartDataProps);
        const totalQty = userEnteredQuantity + inCart;
        const exceedsMaxQuantity = totalQty > maxQuantity;
        let disableAddToCartButton = false;

        /** @type {string|null} */
        let inCartMessage = null;

        /** @type {string|null} */
        let restrictionMessage = null;

        /** @type {string|null} */
        let noticeMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_NOTICE)
            ?.message;

        let backorderMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_BACKORDER)
            ?.message;

        // Check the max quantity restriction first.
        if (exceedsMaxQuantity) {
            const maxQuantityRestriction = restrictionMessages.find(
                x => x.restrictionType === RESTRICTION_TYPE_MAX_QUANTITY
            );

            restrictionMessage = maxQuantityRestriction?.message;
            disableAddToCartButton = true;
        } else {
            // See if there are other blocking restrictions.
            const blockingRestrictions = restrictionMessages.filter(x => x.blocksOrdering);

            if (blockingRestrictions.length > 0) {
                restrictionMessage = blockingRestrictions[0].message;
                disableAddToCartButton = true;
            }
            if (inCart === maxQuantity) {
                disableAddToCartButton = true;
            }
        }

        // If the in-cart quantity is at the max, do not enable the add-to-cart
        // button.
        if (inCart === maxQuantity) {
            disableAddToCartButton = true;
        }

        let showAddToCartButton = !(isDigitalDownload && partType === "NON");
        let showDownloadButton = downloadUrl !== null && downloadUrl !== "";

        /** @type {ProductHeroRestrictionData} */
        const result = {
            containerType: "product-hero",
            partNo,
            partType,
            userEnteredQuantity,
            inCartQuantity: inCart,
            $container,
            $target,
            $addToCartBtn: $("#" + addToCartButtonId),
            $downloadBtn,
            $inCartMessageContainer,
            $quantityElement,
            $restrictionMessageContainer,
            $notInCartIcon,
            $inCartIcon,
            $quantityBadgeElement,
            $noticeMessageContainer,
            $backOrderMessageContainer,
            $userFavoriteContainer,
            showAddToCartButton,
            showDownloadButton,
            disableAddToCartButton,
            restrictionMessage,
            inCartMessage,
            noticeMsg,
            backorderMsg,
            maxQuantity,
            exceedsMaxQuantity,
            isUserFavorite,
            isFeatured,
            isNewArrival,
            isDigitalDownload,
            downloadUrl,
        };

        return result;
    }

    /**
     * Gets the expected actions for the line-item add-to-cart container.
     * @param {JQuery<HTMLElement>} $target The element triggering
     * the restriction check.
     * @param {number} userEnteredQuantity The quantity of the part to add to
     * the cart.
     * @param {JQuery<HTMLElement>} $container
     * @returns {LineItemRestrictionData}
     */
    function determineLineItemSearchAddToCartRestrictions ($target, userEnteredQuantity, $container) {
        const $quantityElement = $container.find(".quantity-select");
        const $restrictionMessageContainer = $container.find(".alert-text");
        const $restrictionAlert = $container.find(".alert");
        const $partDataElement = $container.find(".item-part-number-data");
        const $notInCartIcon = $container.find(".not-in-cart-icon");
        const $inCartIcon = $container.find(".in-cart-icon");
        const $quantityBadgeElement = $container.find(".in-cart-count-badge");
        const $noticeMessageContainer = $container.find(".notice-message");

        // Error if the $partDataElement was unable to be located.
        if ($partDataElement.length === 0) {
            throw new Error("Failed to lookup the part data element.");
        }

        let {
            partMaxquantity: maxQuantityStr,
            addBtn: addToCartButtonId,
            incart: inCartStr,
            part: partNo,
            partType: partType,
            isNewArrival: isNewArrivalStr,
            isFeatured: isFeaturedStr,
            ...remainingPartDataProps
        } = $partDataElement[0].dataset;

        let inCart = parseInt(inCartStr);
        const isNewArrival = isNewArrivalStr === "true";
        const isFeatured = isFeaturedStr === "true";
        let maxQuantity = 2000;

        if (Number.isNaN(inCart)) {
            inCart = 0;
        }

        if (typeof(maxQuantityStr) === "string") {
            maxQuantity = parseInt(maxQuantityStr);
        } else if (typeof(maxQuantityStr) === "number") {
            maxQuantity = maxQuantityStr;
        }

        const restrictionMessages = getRestrictionMessagesFromPartData(remainingPartDataProps);
        const totalQty = userEnteredQuantity;
        const exceedsMaxQuantity = totalQty > maxQuantity;
        let preventsCheckout = false;

        /** @type {string|null} */
        let restrictionMessage = null;

        /** @type {string|null} */
        let noticeMsg = restrictionMessages
            .find(x => x.restrictionType === RESTRICTION_TYPE_NOTICE)
            ?.message;


        // Check the max quantity restriction first.
        if (exceedsMaxQuantity) {
            const maxQuantityRestriction = restrictionMessages.find(
                x => x.restrictionType === RESTRICTION_TYPE_MAX_QUANTITY
            );

            restrictionMessage = maxQuantityRestriction?.message;
            preventsCheckout = true;
        } else {
            // See if there are other blocking restrictions.
            const blockingRestrictions = restrictionMessages.filter(x => x.blocksOrdering);

            if (blockingRestrictions.length > 0) {
                restrictionMessage = blockingRestrictions[0].message;
                preventsCheckout = true;
            }
        }

        /** @type {LineItemRestrictionData} */
        const result = {
            containerType: "lineitem",
            partNo,
            partType,
            userEnteredQuantity,
            inCartQuantity: inCart,
            $container,
            $target,
            $quantityElement,
            $restrictionAlert,
            $restrictionMessage: $restrictionMessageContainer,
            $noticeMessageContainer,
            $notInCartIcon,
            $inCartIcon,
            $quantityBadgeElement,
            preventsCheckout,
            restrictionMessage,
            noticeMsg,
            maxQuantity,
            exceedsMaxQuantity,
            isNewArrival,
            isFeatured,
            isDigitalDownload: false,
        };

        return result;
    }

    /**
     * Retrieves the restriction messages properties from the partData
     * argument.
     * @param {Object} partData 
     * @returns {PartRestriction[]}
     */
    function getRestrictionMessagesFromPartData(partData) {
        /** @type {PartRestriction[]} */
        const restrictionMessages = [];
        /** @type {string[]} */
        const restrictionKeys = [];

        const restrictionMessageKeys = Object.keys(partData).filter(x => x.startsWith("partRestrictionMessage"));

        for (let restrictionMessageKey of restrictionMessageKeys) {
            /** @type {string} */
            const key = restrictionMessageKey
                .replace("partRestrictionMessage", "")
                .replace("BlocksOrdering", "");

            if (key.length === 0) {
                continue;
            } else if (restrictionKeys.includes(key)) {
                continue;
            }

            restrictionKeys.push(key);
        }

        for (let restrictionKey of restrictionKeys) {
            const message = partData["partRestrictionMessage" + restrictionKey] ?? null;
            const blocksOrdering = partData["partRestrictionMessage" + restrictionKey + "BlocksOrdering"] ?? false;

            /** @type {PartRestriction} */
            const restriction = {
                restrictionType: restrictionKey.toLocaleUpperCase(),
                message,
                blocksOrdering: site.Utilities.stringToBool(blocksOrdering),
            };

            restrictionMessages.push(restriction);
        }

        return restrictionMessages;
    }

    /**
     * Updates the product-hero view of a product.
     * @param {ProductHeroRestrictionData} data 
     * @returns {void}
     */
    function updateProductHeroAddToCartHtml(data) {
        if (data.showAddToCartButton) {
            data.$addToCartBtn.removeClass("d-none");

            // Need to move the add-to-cart button to be inside the btn-group.
            const $parent = data.$addToCartBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$addToCartBtn.remove();
                $btnGroup.append(data.$addToCartBtn);
            }
        } else {
            data.$addToCartBtn.addClass("d-none");

            // Need to add the add-to-cart button to be outside the btn-group
            // if not already in it.
            const $parent = data.$addToCartBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$addToCartBtn.remove();
                $addDest.append(data.$addToCartBtn);
            }
        }

        data.$addToCartBtn.prop("disabled", data.disableAddToCartButton);

        if (data.showDownloadButton) {
            data.$downloadBtn
                .attr("href", data.downloadUrl)
                .removeClass("d-none")
                .removeClass("disabled")
                .attr("disabled", null);

            // Need to move the download button to be inside the btn-group.
            const $parent = data.$downloadBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$downloadBtn.remove();
                $btnGroup.append(data.$downloadBtn);
            }
        } else {
            data.$downloadBtn
                .attr("href", "#")
                .addClass("d-none")
                .addClass("disabled")
                .attr("disabled", "disabled");

            // Need to add the download button to be outside the btn-group if
            // not already in it.
            const $parent = data.$downloadBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$downloadBtn.remove();
                $addDest.append(data.$downloadBtn);
            }
        }

        var selectedPartData = data.$container.find("option:selected").data();

        data.$quantityElement
            .attr("max", selectedPartData.partMaxquantity)
            .prop("disabled", !data.showAddToCartButton);

        /** @type {"restriction"|"incart"|"none"} */
        let state = "none";

        if ((data.restrictionMessage ?? "") !== "") {
            state = "restriction";
        } else if ((data.inCartMessage ?? "") !== "") {
            state = "incart";
        } else {
            state = "none";
        }

        if (data.noticeMsg) {
            data.$noticeMessageContainer.html("<i class='fas fa-solid m-1 fa-info-circle warningMsg lh-lg ps-0 ms-0'></i>" + data.noticeMsg);
        } else {
            data.$noticeMessageContainer.html("");
        }
        if (data.backorderMsg) {
            data.$backOrderMessageContainer.html("<i class='fas fa-solid me-1 fa-info-circle warningMsg ps-0 ms-0 lh-lg'></i>" + data.backorderMsg);
            data.$backOrderMessageContainer.removeClass('d-flex')
        } else {
            data.$backOrderMessageContainer.html("");
        }

        data.$userFavoriteContainer.data().partno = data.partNo
        data.$userFavoriteContainer.data().favorite = data.isUserFavorite

        if (data.isUserFavorite) {
            data.$userFavoriteContainer
                .attr("data-favorite", "true")
                .attr("data-partno", data.partNo)
                .find("svg")
                .replaceWith('<i class="fas fa-solid fa-heart fav">');
           
        } else {
            data.$userFavoriteContainer
                .attr("data-favorite", "false")
                .attr("data-partno", data.partNo)
                .find("svg")
                .replaceWith('<i class="far fa-regular fa-heart no-fav">');
        }

        if (state === "restriction") {
            //changes the icon based on if blocks the order
            if (data.disableAddToCartButton) {
                data.$restrictionMessageContainer.html("<i class='fas fa-solid ms-0 ps-0 me-1 fa-exclamation-triangle warningMsg lh-lg'></i><p class='mb-0' style='color: #bd5531;'>" + data.restrictionMessage + "</p>").removeClass("d-none");
            } else {
                data.$restrictionMessageContainer.html("<i class='fas fa-solid m-1 fa-info-circle warningMsg lh-lg'></i><p class='mb-0'>" + data.restrictionMessage + "</p>").removeClass("d-none");
            }
            data.$inCartMessageContainer.text(data.inCartMessage).removeClass("d-none");
        } else if (state === "incart") {
            data.$restrictionMessageContainer.text(null).addClass("d-none");
            //This line below causes a content layout shift on the productDetails page when switching between products within the dropdown
            //It appears the inCartMessageContainer is not set properly or displayed during this time which is why it's not set.
            //This is tested on product 0635
            data.$inCartMessageContainer.text(data.inCartMessage).removeClass("d-none");
        } else if (state === "none") {
            data.$restrictionMessageContainer.text(null).addClass("d-none");
            data.$inCartMessageContainer.text(null).addClass("d-none");
        } else {
            throw new Error("Not implemented.")
        }

        // Update the in-cart icon and the badge.
        if (data.inCartQuantity > 0) {
            data.$notInCartIcon.addClass("d-none");
            data.$inCartIcon.removeClass("d-none");
            data.$quantityBadgeElement.removeClass("d-none").text(data.inCartQuantity);
        } else {
            data.$notInCartIcon.removeClass("d-none");
            data.$inCartIcon.addClass("d-none");    
            data.$quantityBadgeElement.addClass("d-none").text("0");
        }
    }

    /**
     * Updates the product-hero view of a product.
     * @param {LineItemRestrictionData} data 
     * @returns {void}
     */
    function updateLineItemAddToCartHtml (data) {
        if ((data.restrictionMessage ?? "") !== "") {
            data.$restrictionMessage.text(data.restrictionMessage);
            data.$restrictionAlert.removeClass("d-none");

            if (data.preventsCheckout) {
                data.$restrictionAlert
                    .removeClass("cart-item-alert-grey-msg")
                    .addClass("cart-item-alert-msg");
            } else {
                data.$restrictionAlert
                    .removeClass("cart-item-alert-msg")
                    .addClass("cart-item-alert-grey-msg");
            }
        } else {
            data.$restrictionAlert
                .removeClass("cart-item-alert-msg")
                .removeClass("cart-item-alert-grey-msg")
                .addClass("d-none");

            data.$restrictionMessage.text("");
        }
    }

    /**
     * Updates the tile view of a product.
     * @param {TileRestrictionData} data 
     * @returns {void}
     */
    function updateTileAddToCartHtml (data) {
        const $featuredOverlay = data.$container.find(".featured-overlay");

        if (!data.isFeatured && !data.isNewArrival) {
            $featuredOverlay.addClass("d-none");
        } else {
            $featuredOverlay
                .removeClass("d-none")
                .find(".featured-product-banner > small")
                .text(data.isNewArrival ? "New" : "Featured");
        }

        if (data.showAddToCartButton) {
            data.$addToCartBtn.removeClass("d-none");

            // Need to move the add-to-cart button to be inside the btn-group.
            const $parent = data.$addToCartBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$addToCartBtn.remove();
                $btnGroup.append(data.$addToCartBtn);
            }
        } else {
            data.$addToCartBtn.addClass("d-none");

            // Need to add the add-to-cart button to be outside the btn-group
            // if not already in it.
            const $parent = data.$addToCartBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$addToCartBtn.remove();
                $addDest.append(data.$addToCartBtn);
            }
        }

        data.$addToCartBtn.prop("disabled", data.disableAddToCartButton);

        if (data.showDownloadButton) {
            data.$downloadBtn
                .attr("href", data.downloadUrl)
                .removeClass("d-none")
                .removeClass("disabled")
                .attr("disabled", null);

            // Need to move the download button to be inside the btn-group.
            const $parent = data.$downloadBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$downloadBtn.remove();
                $btnGroup.append(data.$downloadBtn);
            }
        } else {
            data.$downloadBtn
                .attr("href", "#")
                .addClass("d-none")
                .addClass("disabled")
                .attr("disabled", "disabled");

            // Need to add the download button to be outside the btn-group if
            // not already in it.
            const $parent = data.$downloadBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$downloadBtn.remove();
                $addDest.append(data.$downloadBtn);
            }
        }

        data.$quantityElement
            .attr("max", data.maxQuantity)
            .prop("disabled", !data.showAddToCartButton);

        // NOTE: Can display the cart message AND the restriction

        /** @type {"restriction"|"incart"|"none"} */
        let state = "none";

        if ((data.restrictionMessage ?? "") !== "") {
            state = "restriction";
        } else if ((data.inCartMessage ?? "") !== "") {
            state = "incart";
        } else {
            state = "none";
        }

        const infoCircleHtml = "<i class='fas fa-solid fa-info-circle warningMsg lh-lg'></i>";

        // Display notice message if it exists for the selected product.
        if (data.noticeMsg) {
            data.$noticeMessageContainer.html(infoCircleHtml + data.noticeMsg);
        } else {
            data.$noticeMessageContainer.html("");
        }

        if (data.backorderMsg) {
            $(data.$backOrderMessageContainer).html(infoCircleHtml + data.backorderMsg);
        } else {
            $(data.$backOrderMessageContainer).html("");
        }

        if (data.isUserFavorite) {
            data.$userFavoriteContainer
                .attr("data-favorite", "true")
                .attr("data-partno", data.partNo)
                .find("svg")
                .replaceWith('<i class="fas fa-solid fa-heart fav">');
        } else {
            data.$userFavoriteContainer
                .attr("data-favorite", "false")
                .attr("data-partno", data.partNo)
                .find("svg")
                .replaceWith('<i class="far fa-regular fa-heart no-fav">');
        }

        if (state === "restriction") {
            //changes the icon based on if blocks the order
            if (data.disableAddToCartButton) {
                data.$restrictionMessageContainer.html("<i class='fas fa-solid me-1 fa-exclamation-triangle warningMsg lh-lg'></i><p class='mb-0' style='color: #bd5531;'>" + data.restrictionMessage + "</p>").removeClass("d-none");
            } else {
                data.$restrictionMessageContainer.html("<i class='fas fa-solid me-1 fa-info-circle warningMsg lh-lg'></i><p class='mb-0'>" + data.restrictionMessage + "</p>").removeClass("d-none");
            }
            data.$inCartMessageContainer.text(data.inCartMessage).removeClass("d-none");
        } else if (state === "incart") {
            data.$restrictionMessageContainer.text(null).addClass("d-none");
            data.$inCartMessageContainer.text(data.inCartMessage).removeClass("d-none");
        } else if (state === "none") {
            data.$restrictionMessageContainer.text(null).addClass("d-none");
            data.$inCartMessageContainer.text(null).addClass("d-none");
        } else {
            throw new Error("Not implemented.")
        }

        // Update the in-cart icon and the badge.
        if (data.showAddToCartButton) {
            if (data.inCartQuantity > 0) {
                data.$notInCartIcon.addClass("d-none");
                data.$inCartIcon.removeClass("d-none");
                data.$quantityBadgeElement.removeClass("d-none").text(data.inCartQuantity);
            } else {
                data.$notInCartIcon.removeClass("d-none");
                data.$inCartIcon.addClass("d-none");
                data.$quantityBadgeElement.addClass("d-none").text("0");
            }
        } else {
            data.$notInCartIcon.addClass("d-none");
            data.$inCartIcon.addClass("d-none");
            data.$quantityBadgeElement.addClass("d-none").text("0");
        }
    }

    /**
     * Updates the list view of a product.
     * @param {ListRestrictionData} data 
     * @returns {void}
     */
    function updateListAddToCartHtml(data) {
        data.$addToCartBtn.prop("disabled", data.disableAddToCartButton);

        if (data.showAddToCartButton) {
            data.$addToCartBtn.removeClass("d-none");
        } else {
            data.$addToCartBtn.addClass("d-none");
        }

        if (data.showDownloadButton) {
            data.$downloadBtn
                .attr("href", data.downloadUrl)
                .removeClass("d-none");

            // Need to move the download button to be inside the btn-group.
            const $parent = data.$downloadBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$downloadBtn.remove();
                $btnGroup.append(data.$downloadBtn);
            }
        } else {
            data.$downloadBtn
                .attr("href", "#")
                .addClass("d-none");

            // Need to add the download button to be outside the btn-group if
            // not already in it.
            const $parent = data.$downloadBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$downloadBtn.remove();
                $addDest.append(data.$downloadBtn);
            }
        }

        const $featuredOverlay = data.$container.find(".featured-overlay");

        if (!data.isFeatured && !data.isNewArrival) {
            $featuredOverlay.addClass("d-none");
        } else {
            $featuredOverlay
                .removeClass("d-none")
                .find(".featured-product-banner > small")
                .text(data.isNewArrival ? "New" : "Featured");
        }
        
        // NOTE: Can only display the cart message OR the restriction message,
        // or neither of them if no messages for either. But they should never
        // both be displayed. Also, when displaying the restriction message
        // make sure to hide the add-to-cart button.

        // NOTE: Also in this context the data.$restrictionMessageContainer
        // should be the same element as the data.$inCartMessageContainer
        // element.

        /** @type {"restriction"|"incart"|"none"} */
        let state = "none";

        if ((data.restrictionMessage ?? "") !== "") {
            state = "restriction";
        } else if ((data.inCartMessage ?? "") !== "") {
            state = "incart";
        } else {
            state = "none";
        }

        // Display notice message if it exists for the selected product.
        if (data.noticeMsg) {
            data.$noticeMessageContainer.html("<i class='fas fa-solid fa-info-circle warningMsg lh-lg'></i>" + data.noticeMsg);
        } else {
            data.$noticeMessageContainer.html("");
        }

        if (data.backorderMsg) {
            data.$backOrderMessageContainer.html("<i class='fas fa-solid fa-info-circle warningMsg lh-lg'></i>" + data.backorderMsg);
        } else {
            data.$backOrderMessageContainer.html("");
        }

        if (state === "restriction") {
            if (data.disableAddToCartButton) {
                data.$inCartMessageContainer.html("<i class='fas fa-solid me-1 fa-exclamation-triangle warningMsg lh-lg'></i><p class='mb-0' style='color: #bd5531;'>" + data.restrictionMessage + "</p>").removeClass("d-none");
                data.$inCartMessageContainer.parent().removeClass('d-none');
            } else {
                data.$inCartMessageContainer.parent().removeClass('d-none');
                data.$inCartMessageContainer.html("<i class='fas fa-solid me-1 fa-info-circle warningMsg lh-lg'></i><p class='mb-0'>" + data.restrictionMessage + "</p>").removeClass("d-none");
            }
        } else if (state === "incart") {
            data.$inCartMessageContainer.text(data.inCartMessage).removeClass("d-none");
        } else if (state === "none") {
            data.$inCartMessageContainer.text(null).addClass("d-none");
        } else {
            throw new Error("Not implemented.");
        }

        if (data.showQuantityInput) {
            data.$quantityElement.removeClass("d-none");
        } else {
            data.$quantityElement.addClass("d-none");
        }

        data.$quantityElement.prop("disabled", !data.showAddToCartButton);

        // Update the in-cart icon and the badge.
        if (data.showAddToCartButton) {
            if (data.inCartQuantity > 0) {
                data.$notInCartIcon.addClass("d-none");
                data.$inCartIcon.removeClass("d-none");
                data.$quantityBadgeElement.removeClass("d-none").text(data.inCartQuantity);
            } else {
                data.$notInCartIcon.removeClass("d-none");
                data.$inCartIcon.addClass("d-none");
                data.$quantityBadgeElement.addClass("d-none").text("0");
            }
        } else {
            data.$notInCartIcon.addClass("d-none");
            data.$inCartIcon.addClass("d-none");
            data.$quantityBadgeElement.addClass("d-none").text("0");
        }
    }

    /**
     * Updates the global view of a product.
     * @param {GlobalSearchRestrictionData} data 
     * @returns {void}
     */
    function updateGlobalAddToCartHtml (data) {
        if (data.showAddToCartButton) {
            data.$addToCartBtn.removeClass("d-none");

            // Need to move the add-to-cart button to be inside the btn-group.
            const $parent = data.$addToCartBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$addToCartBtn.remove();
                $btnGroup.append(data.$addToCartBtn);
            }
        } else {
            data.$addToCartBtn.addClass("d-none");

            // Need to add the add-to-cart button to be outside the btn-group
            // if not already in it.
            const $parent = data.$addToCartBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$addToCartBtn.remove();
                $addDest.append(data.$addToCartBtn);
            }
        }

        data.$addToCartBtn.prop("disabled", data.disableAddToCartButton);

        if (data.disableAddToCartButton) {
            data.$addToCartBtn.addClass("disabled");
        } else {
            data.$addToCartBtn.removeClass("disabled");
        }

        if (data.showDownloadButton) {
            data.$downloadBtn
                .attr("href", data.downloadUrl)
                .removeClass("disabled")
                .attr("disabled", "disabled")
                .removeClass("d-none");

            // Need to move the download button to be inside the btn-group.
            const $parent = data.$downloadBtn.parent();

            if (!$parent.is(".btn-group")) {
                const $btnGroup = data.$container.find(".btn-group");
                data.$downloadBtn.remove();
                $btnGroup.append(data.$downloadBtn);
            }
        } else {
            data.$downloadBtn
                .attr("href", "#")
                .addClass("disabled")
                .attr("disabled", null)
                .addClass("d-none");

            // Need to add the download button to be outside the btn-group if
            // not already in it.
            const $parent = data.$downloadBtn.parent();

            if ($parent.is(".btn-group")) {
                const $addDest = $parent.parent();
                data.$downloadBtn.remove();
                $addDest.append(data.$downloadBtn);
            }
        }

        data.$quantityElement
            .attr("max", data.maxQuantity)
            .prop("disabled", !data.showAddToCartButton);

        const $featuredOverlay = data.$container.find(".featured-overlay");

        if (!data.isFeatured && !data.isNewArrival) {
            $featuredOverlay.addClass("d-none");
        } else {
            $featuredOverlay
                .removeClass("d-none")
                .find(".featured-product-banner > small")
                .text(data.isNewArrival ? "New" : "Featured");
        }

        // NOTE: Can only display both the cart message and the restriction
        // message.

        if (data.noticeMsg) {
            data.$noticeMessageContainer
                .html("<i class='fas fa-solid me-1 mt-1 fa-info-circle m-1 warningMsg lh-lg'></i><p class=\"mb-0\">" + data.noticeMsg + "</p>")
                .removeClass("d-none");
        } else {
            data.$noticeMessageContainer
                .html("")
                .addClass("d-none");
        }

        if ((data.restrictionMessage ?? "") !== "") {
            //changes the icon based on if blocks the order
            if (data.disableAddToCartButton) {
                data.$restrictionMessageContainer
                    .html("<i class='fas fa-solid mt-1 me-1 fa-exclamation-triangle m-1 warningMsg lh-lg'></i><p class='mb-0' style='color: #bd5531;'>" + data.restrictionMessage + "</p>")
                    .removeClass("d-none");
            } else {
                data.$restrictionMessageContainer
                    .html("<i class='fas fa-solid me-1 mt-1 fa-info-circle m-1 warningMsg lh-lg'></i><p class='mb-0'>" + data.restrictionMessage + "</p>")
                    .removeClass("d-none");
            }
            data.$inCartMessageContainer.text(data.inCartMessage).removeClass("d-none");
        } else {
            data.$restrictionMessageContainer.html("").addClass("d-none");
        }

        // Can only display the back order message when there is no restriction
        // message.
        if ((data.backorderMsg ?? "") !== "" && (data.restrictionMessage ?? "") === "") {
            data.$restrictionMessageContainer
                .html("<i class='fas fa-solid fa-info-circle m-1 warningMsg lh-lg'></i><p class=\"mb-0\">" + data.backorderMsg + "</p>")
                .removeClass("d-none");
        } else if ((data.backorderMsg ?? "") === "" && (data.restrictionMessage ?? "") === "") {
            data.$restrictionMessageContainer
                .html("")
                .addClass("d-none");
        }

        // Update the in-cart icon and the badge.
        if (data.showAddToCartButton) {
            if (data.inCartQuantity > 0) {
                data.$notInCartIcon.addClass("d-none");
                data.$inCartIcon.removeClass("d-none");
                data.$quantityBadgeElement.removeClass("d-none").text(data.inCartQuantity);
            } else {
                data.$notInCartIcon.removeClass("d-none");
                data.$inCartIcon.addClass("d-none");
                data.$quantityBadgeElement.addClass("d-none").text("0");
            }
        } else {
            data.$notInCartIcon.addClass("d-none");
            data.$inCartIcon.addClass("d-none");
            data.$quantityBadgeElement.addClass("d-none").text("0");
        }

        if (data.isUserFavorite) {
            data.$userFavoriteContainer
                .attr("data-favorite", "true")
                .attr("data-partno", data.partNo)
                .find("svg")
                .replaceWith('<i class="fas fa-solid fa-heart fav">');
        } else {
            data.$userFavoriteContainer
                .attr("data-favorite", "false")
                .attr("data-partno", data.partNo)
                .find("svg")
                .replaceWith('<i class="far fa-regular fa-heart no-fav">');
        }

    }

    /**
     * Updates the html of the products container (tile/list/global) element.
     * @param {RestrictionData} data 
     * @returns {void}
     */
    var displayBtnAndMessages = function (data) {
        if (data.containerType === "tile") {
            updateTileAddToCartHtml(data);
        } else if (data.containerType === "list") {
            updateListAddToCartHtml(data);
        } else if (data.containerType === "global") {
            updateGlobalAddToCartHtml(data);
        } else if (data.containerType === "product-hero") {
            updateProductHeroAddToCartHtml(data);
        } else if (data.containerType === "lineitem") {
            updateLineItemAddToCartHtml(data);
        } else {
            throw new Error("Not implemented.");
        }
    }

    var addFavoritePartAsync = function (partNo, abortSignal) {
        if (partNo === null || partNo === undefined || partNo === "") {
            throw new Error("The argument 'productid' cannot be null/undefined/empty.");
        }

        const url = "/Account/AddFavoritePart?partNo=" + partNo;

        return fetch(url, {
            method: "GET",
            signal: abortSignal,
            headers: {
                "Content-Type": "application/json"
            },
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error("Failure - Could not add Favorite");
                }
                return partNo;
            });
    }

    var removeFavoritePartAsync = function (partNo, abortSignal) {
        if (partNo === null || partNo === undefined || partNo === "") {
            throw new Error("The argument 'productid' cannot be null/undefined/empty.");
        }

        const url = "/Customer/RemoveFavoritePart?partNo=" + partNo;

        return fetch(url, {
                method: "GET",
            })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error("Failure - Could not add Favorite");
                }
        
                return response.json();
            });
    }

    var removeFavoriteProductAsync = function (productid, abortSignal) {
        if (productid === null || productid === undefined || productid === "") {
            throw new Error("The argument 'productid' cannot be null/undefined/empty.");
        }

        const url = "/Customer/RemoveFavoriteProduct?productid=" + productid;

        return fetch(url, {
                method: "GET",
            })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error("Failure - Could not add Favorite");
                }
        
                return response.json();
            });
    }

    var removeFavoriteProductAsync = function (productid, abortSignal) {
        if (productid === null || productid === undefined || productid === "") {
            throw new Error("The argument 'productid' cannot be null/undefined/empty.");
        }

        const url = "/Customer/RemoveFavoriteProduct?productid=" + productid;

        return fetch(url, {
                method: "GET",
            })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error("Failure - Could not remove favorite product.");
                }
        
                return response.json();
            });
    }

    var refreshMobileListView = function () {
        $("[data-add-to-cart-container-type='list']").each(function () {
            // Check if the record had a blocking restriction that is
            // NOT the max quantity.
            const $this = $(this);
            const $quantityElement = $this.find(".quantity-select");
            let selected = $quantityElement.val();

            if (typeof(selected) === "string") {
                selected = parseInt(selected);
            }

            if (isNaN(selected)) {
                selected = 0;
            }

            const restrictionData = determineAddToCartElementsRestrictions($quantityElement, selected);
            displayBtnAndMessages(restrictionData);
        });
    }

    //#endregion

    $(document).on('change', '.quantity-select', function () {
        const $this = $(this);
        let selected = $this.val();

        if (typeof(selected) !== "number") {
            selected = parseInt(selected);
        }

        const restrictions = determineAddToCartElementsRestrictions($this, selected);
        displayBtnAndMessages(restrictions);
    });

    $(document).on('change', '.parts-dropdown', function () {
        let partNo = $(this).find(":selected").data().part
        let favoriteIcon;
        let isUserFavorite;

        if (!$('#product-details-page').is(':visible')) {
            favoriteIcon = $(`.js-favorite[data-partno=${partNo}]`)
            isUserFavorite = site.Utilities.stringToBool($(this).find(":selected").data().userFavorite)
        }
        else {
            favoriteIcon = $('#product-details-page').find(`.js-favorite svg`)
            isUserFavorite = site.Utilities.stringToBool($(this).find(":selected").data().userFavorite)
        }

        if (isUserFavorite) {
            favoriteIcon.removeClass('far fa-regular fa-heart no-fav');
            favoriteIcon.addClass('fas fa-solid fa-heart fav');
        }
        else {
            favoriteIcon.removeClass('fas fa-solid fa-heart fav');
            favoriteIcon.addClass('far fa-regular fa-heart no-fav');
        }

        let wrapperParent = $(this).closest('.add-to-cart-wrapper');
        let quantityContainer = $(wrapperParent).find(".quantity-select");
        let value = quantityContainer.val();
        let restrictions = determineAddToCartElementsRestrictions(this, value);

        // Select the new badge element update quantity
        // Update icon for is/is not in cart.
        displayBtnAndMessages(restrictions);
    });

    // TODO: Modernize function call.
    $(document).on('click', '.quick-order-part', function (e) {
        var number = $(this).attr('data-part-number');
        var description = $(this).attr('data-part-description');
        var max = $(this).attr('data-part-maxquantity');
        var maxMessage = $(this).attr('data-part-maxQuantity-message');
        var restriction = $(this).attr('data-part-restriction-message');
        var blocked = $(this).attr('data-part-blocking');
        var inCart = $(this).attr('data-part-inCart');

        $('#product-search-input').val(number + " " + description);
        $('#product-search-input').attr('data-part-number', number);
        $('#product-search-input').attr('data-part-incart', inCart);
        $('#product-search-input').attr('data-part-description', description);
        $('#product-search-input').attr('data-part-max-quantity', max);
        $('#product-quantity-input').attr('max', max);
        $('#product-quantity-input').attr('data-part-restriction-message', restriction);
        $('#product-search-input').attr('data-part-blocking', blocked);
        $('#product-quantity-input').attr('data-part-maxQuantity-message', maxMessage);
        $('#part-search-container').addClass('d-none');
        $('.toast-body').find('p').find('strong').text(description);
        //site.Cart has not been updated with the new format of our JS
        site.Cart.checkQuickOrderCartBtn();
        $('#product-quantity-input').val("");
        $('#product-quantity-input').focus();

        if (restriction != "") {
            $('#search-alert-msg').text(restriction);
            $('#search-alert-msg').removeClass('d-none');
        } else {
            $('#search-alert-msg').addClass('d-none');
        }
    });

    $(document).on("click", ".js-favorite-product:not(.disabled)", function () {
        const $jsFavoriteButton = $(this);
        const $svg = $jsFavoriteButton.find("svg");
        const $spinnerEl = $('<div class="spinner-border"></div>');
        const productId = $jsFavoriteButton.data("productId");
        $jsFavoriteButton.addClass("disabled");
        $spinnerEl.prependTo($jsFavoriteButton);
        $svg.addClass("d-none");

        removeFavoriteProductAsync(productId)
            .then(function () {
                console.log("Done!");
            })
            .catch(function (error) {
                site.Cart.makeToast("Error", "Failed to update the favorite status of the product.");
                console.error(error);
            })
            .finally(function () {
                $jsFavoriteButton.removeClass("disabled");
                $spinnerEl.remove();
                $svg.removeClass("d-none");
            });
    });

    $(document).on('click', ".js-favorite:not(.disabled)", function () {
        const $jsFavoriteButton = $(this);
        const $svg = $jsFavoriteButton.find("svg");
        const $spinnerEl = $('<div class="spinner-border"></div>');
        var $standardProductDataContainer;
        var partNo;
        var isFavorited;

        if (!$('#product-details-page').is(':visible')) {
            $standardProductDataContainer = $jsFavoriteButton
                .closest("[data-add-to-cart-container-type]")
                .find("[standard-product-data-attributes]");

            partNo = $jsFavoriteButton.data("partno").toString();
            isFavorited = $jsFavoriteButton.data('favorite') === true;
        }
        else {
            $standardProductDataContainer = $("[standard-product-data-attributes]").filter(':selected')
            partNo = $standardProductDataContainer.data().part;
            isFavorited = $standardProductDataContainer.data().userFavorite;
        }

        const productId = $standardProductDataContainer.data("productid")
        let products = $('.js-favorite[data-partno="' + partNo + '"]');
        $jsFavoriteButton.addClass("disabled");
        $spinnerEl.prependTo($jsFavoriteButton);
        $svg.addClass("d-none");

        if (isFavorited) {
            // If unfavoriting, for historic reasons unfavorite the product as
            // well as the part. Going forward only parts should be favorited
            if ((productId != null || productId != undefined) && productId?.length > 0) {
                const removeProductPromise = removeFavoriteProductAsync(productId, null);

            }
            const removePartPromise = removeFavoritePartAsync(partNo, null).then(function () {
                for (var item of products) {
                    $(item).html('<i class="far fa-regular fa-heart no-fav">');
                    $(item).data('favorite', false);
                }

                $standardProductDataContainer
                    .attr("data-user-favorite", "false")
                    .data("userFavorite", false);
            })
                .catch(function (error) {
                    site.Cart.makeToast("Error", "Failed to update the favorite status of the product.");
                    console.error(error);
                })
                .finally(function () {
                    $jsFavoriteButton.removeClass("disabled");
                    $spinnerEl.remove();
                    $svg.removeClass("d-none");
                });
           
        } else {
            addFavoritePartAsync(partNo, null)
                .then(function (partNo) {
                    for (var item of products) {
                        if ($(item).data().partno == partNo)
                            $standardProductDataContainer.each(function (index, part) {
                                if ($(part).data().part == partNo) {
                                    $(part).attr("data-user-favorite", "true")
                                        .data("userFavorite", true);
                                }
                            })
                    }
                    $(`.js-favorite[data-partno=${partNo}]`).data().favorite = true;
                    $(`.js-favorite[data-partno=${partNo}]`).html('<i class="fas fa-solid fa-heart fav">');
                }
                )
                .catch(function (error) {
                    site.Cart.makeToast("Error", "Failed to update the favorite status of the product.");
                    console.error(error);
                })
                .finally(function () {
                    $jsFavoriteButton.removeClass("disabled");
                    $spinnerEl.remove();
                    $svg.removeClass("d-none");
                });
        }
    });

    $(function () {
        // This event listener reacts to the resize event and will update the
        // list view for mobile accordingly.
        mediaQueries.xl.addEventListener("change", function () {
            refreshMobileListView();
        });

        // This event listener reacts to whenever the search results refresh
        // and will also update the list for view mobile accordingly.
        if (window.site.ProductGridComponent) {
            window.site.ProductGridComponent.addEventListener(
                "html-updated",
                refreshMobileListView
            );
        }
    });

    // Prevents the mobile nav submenus and backdrop from remaining
    // visible when the mobile nav is hidden and header nav is active
    $(function () {
        mediaQueries.lg.addEventListener("change", function () {
            $('.offcanvas-backdrop').offcanvas('hide');
            $('.offcanvas-bottom').offcanvas('hide');
        })
    });

    // $(document).on('change', '.quantity-select', function () {
    //     var selected = $(this).val();
    //     var qty = determineAddToCartElementsRestrictions(this, selected);
    //     displayBtnAndMessages(qty);
    // });

    // Return
    return {
        getGoogleAnalyticsData: getGoogleAnalyticsData,
        backgroundImageElement: backgroundImageElement,
        partImageToggle: partImageToggle,
        getUrlParams: getUrlParams,
        setProductLinks: setProductLinks,
        stopVideoOnModalClose: stopVideoOnModalClose,
        bindNumericConstraints: bindNumericConstraints,
        getUrlPathSegments: getUrlPathSegments,
        backToClassicPrep: backToClassicPrep,
        displayToast: displayToast,
        onCheckout: onCheckout,
        stringToBool: stringToBool,
        debounce: debounce,
        debounceV2: debounceV2,
        populateTemplate: populateTemplate,
        determineAddToCartElementsRestrictions: determineAddToCartElementsRestrictions,
        getRestrictionMessagesFromPartData: getRestrictionMessagesFromPartData,
        displayBtnAndMessages: displayBtnAndMessages,
        toggleSortByAndViewBtns: toggleSortByAndViewBtns
    };
}());